import { ChevronRight, Coins, Cpu, FileSpreadsheet, Gauge, History, MessageSquareText, Network, ShieldCheck, Sparkles, Users, type LucideIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { isMaster, isAdministrator } from "@/shared/utils/roles";
import { useAppShell } from "@/shell/app-shell-context";

type AdminItem = { label: string; href: string; icon: LucideIcon; masterOnly: boolean; description: string };

/** As seções da Administração, em grupos. A ordem daqui é a ordem na barra e
 *  na página inicial. `masterOnly`: só o Administrador vê; o Coordenador entra
 *  só pra gerir Usuários e Núcleos. */
const GROUPS: Array<{ title: string; items: AdminItem[] }> = [
  {
    title: "ACESSO",
    items: [
      { label: "Usuários", href: "/admin/usuarios", icon: Users, masterOnly: false, description: "Pessoas, cargos e empresas que cada uma acessa." },
      { label: "Núcleos", href: "/admin/nucleos", icon: Network, masterOnly: false, description: "Times e as empresas de cada um." }
    ]
  },
  {
    title: "INTELIGÊNCIA ARTIFICIAL",
    items: [
      { label: "Mestra Help", href: "/admin/copiloto", icon: Sparkles, masterOnly: true, description: "Prompt, modelo e quebra-gelos do assistente." },
      { label: "Modelos de IA", href: "/admin/modelos-ia", icon: Cpu, masterOnly: true, description: "Provedores, chaves, modelos e preços." },
      { label: "Consumo de IA", href: "/admin/consumo", icon: Coins, masterOnly: true, description: "Chamadas, tokens e custo por período." },
      { label: "Prompts padrão", href: "/admin/prompts-padrao", icon: MessageSquareText, masterOnly: true, description: "Os textos com que toda empresa nova nasce." }
    ]
  },
  {
    title: "PLATAFORMA",
    items: [
      { label: "CENA", href: "/admin/cena", icon: Gauge, masterOnly: true, description: "Pesos e pontos da nota, globais e por empresa." },
      { label: "Templates de upload", href: "/admin/templates-upload", icon: FileSpreadsheet, masterOnly: true, description: "Os formatos de planilha de cada clipadora." },
      { label: "Auditoria", href: "/admin/auditoria", icon: History, masterOnly: true, description: "Quem mudou o quê na configuração." }
    ]
  }
];

function visibleGroups(master: boolean) {
  return GROUPS.map(group => ({ ...group, items: group.items.filter(item => master || !item.masterOnly) })).filter(
    group => group.items.length > 0
  );
}

/**
 * A casca da Administração (2026-10-15): o mesmo desenho de Configurações e de
 * Ajustes — barra lateral simples com grupos e o conteúdo ao lado, na largura
 * toda.
 */
export function AdminLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile } = useAppShell();
  const master = isMaster(profile);
  const authorized = isAdministrator(profile);

  useEffect(() => {
    if (!authorized) navigate("/", { replace: true });
  }, [authorized, navigate]);

  if (!authorized) return null;

  return (
    <div className="ajustes-layout section-shell admin-shell">
      <aside className="ajustes-sidebar">
        <Link to="/admin" className="ajustes-sidebar-header admin-shell-header">
          <div className="company-badge-icon"><ShieldCheck size={16} /></div>
          <div>
            <strong>Administração</strong>
            <span>{master ? "Configuração da instalação" : "Acesso do seu núcleo"}</span>
          </div>
        </Link>
        <nav className="ajustes-nav section-nav" aria-label="Navegação da seção">
          {visibleGroups(master).map(group => (
            <div key={group.title} className="admin-nav-group">
              <div className="ajustes-nav-section-title">{group.title}</div>
              {group.items.map(({ label, href, icon: Icon }) => {
                const active = pathname === href;
                return (
                  <Link
                    key={href}
                    to={href}
                    className={`ajustes-nav-item ${active ? "active" : ""}`}
                    aria-current={active ? "page" : undefined}
                  >
                    <Icon size={16} />
                    <span className="ajustes-nav-item-body">
                      <span className="ajustes-nav-item-label">{label}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
      <main className="ajustes-content section-content">
        <Outlet />
      </main>
    </div>
  );
}

/** A página inicial da Administração: um atalho por seção, nos mesmos grupos
 *  da barra. */
export function AdminHome() {
  const { profile } = useAppShell();
  const master = isMaster(profile);
  return (
    <div className="ajustes-panel admin-home">
      <header className="panel-header">
        <div>
          <h2>Administração</h2>
          <p>
            {master
              ? "O que vale para a instalação inteira: acesso, inteligência artificial e regras da plataforma."
              : "Gerencie as pessoas e as empresas dos núcleos que estão sob a sua administração."}
          </p>
        </div>
      </header>
      {visibleGroups(master).map(group => (
        <section key={group.title} className="panel-section">
          <h3>{group.title.charAt(0) + group.title.slice(1).toLowerCase()}</h3>
          <div className="admin-home-grid">
            {group.items.map(({ label, href, icon: Icon, description }) => (
              <Link key={href} to={href} className="admin-home-card">
                <span className="admin-home-icon"><Icon size={18} /></span>
                <span className="admin-home-text">
                  <strong>{label}</strong>
                  <span>{description}</span>
                </span>
                <ChevronRight className="admin-home-arrow" size={16} />
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
